import { Field, Form, Buttons, CancelButton, ModalCard, ActionButton } from '~/components';
import { useApi, useToast } from '~/contexts';
import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useForm } from '~/hooks';
import styled from 'styled-components';
import { colors } from '~/styles';
import moment from 'moment';
import { dateFormats } from '~/utils';

const FeedbackSubject = styled.div`
  font-size: 0.875rem;
  color: ${colors.grey55};
  margin-bottom: 0.25rem;
`;

const FeedbackContent = styled.div`
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: ${colors.grey55};
  white-space: pre-wrap;

  &:before {
    content: open-quote;
  }

  &:after {
    content: close-quote;
  }
`;

const Separator = styled.hr`
  margin-bottom: 1.25rem;
  margin-top: 1.25rem;
`;

const FeedbackDetails = styled.div`
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: ${colors.grey55};
`;

const ResponsesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ResponseOption = styled.a`
  font-size: 0.875rem;
`;

const feedbackResponsesArray = [
  {
    name: 'Adding to Backlog',
    response:
      "Thank you for your feedback. We've added it to our product backlog for prioritization and planning. Please keep an eye on What's New to be notified when this enhancement is available.",
  },
  {
    name: 'Requires Further Analysis',
    response:
      'Thank you for your thoughts. Our product team will evaluate this as an option in further detail. However, this enhancement may be a bit of stretch for us at this time. We will keep note of your suggestion and consider it as we continue to improve Ruddr.',
  },
];

function FeedbackDialog({ feedbackItem, onClose, onSaved }) {
  const toast = useToast();
  const api = useApi();
  const [{ isSubmitting }, form] = useForm();

  const handleSubmit = async (values) => {
    form.submit();
    await api.admin.feedbackItems.sendEmail(feedbackItem.id, {
      memberId: feedbackItem.member.id,
      emailBody: values.emailBody,
    });

    await onSaved();
    form.done();
    toast.success('Email has been sent.');
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <ModalCard title="Feedback Response" onClose={handleClose}>
      <Formik
        enableReinitialize
        initialValues={{ emailBody: '' }}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          emailBody: Yup.string().label('Body').max(5000).required(),
        })}>
        {({ values, setFieldValue }) => {
          const handleInsertResponse = (response) => {
            setFieldValue('emailBody', values.emailBody ? `${values.emailBody}\n\n${response}` : response);
          };

          return (
            <Form>
              <ModalCard.Body>
                <FeedbackDetails>
                  <strong>{feedbackItem.member.name}</strong> at {feedbackItem.workspace.name} -{' '}
                  {moment(feedbackItem.createdAt).format(dateFormats.dateTime)}
                </FeedbackDetails>

                <FeedbackSubject>{feedbackItem.subject}</FeedbackSubject>
                <FeedbackContent>{feedbackItem.content}</FeedbackContent>

                <Separator />

                <ResponsesContainer>
                  {feedbackResponsesArray.map(({ name, response }) => (
                    <ResponseOption key={name} onClick={() => handleInsertResponse(response)}>
                      {name}
                    </ResponseOption>
                  ))}
                </ResponsesContainer>

                <Form.Control>
                  <Field.TextArea name="emailBody" placeholder="Body" maxLength={5000} />
                </Form.Control>
              </ModalCard.Body>

              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={handleClose}>Cancel</CancelButton>
                  <ActionButton isLoading={isSubmitting} type="submit">
                    Send Email
                  </ActionButton>
                </Buttons>
              </ModalCard.Footer>
            </Form>
          );
        }}
      </Formik>
    </ModalCard>
  );
}

export default FeedbackDialog;
